import { useEffect, useState } from "react";
import { Pagination } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import AppDialogDefault from "@/components/ui/appDialogDefault/AppDialogDefault.component";
import type { IAuditoriaEventoGrupo } from "@/api/space/dtos/authenticator.dto";
import type { AuditoriaGrupoRef, FiltroAuditoriaAplicado } from "../../../cadastroRestricoes.types";
import FiltrosAuditoria from "./ui/FiltrosAuditoria";
import ListaAuditoria from "./ui/ListaAuditoria";

type ModalAuditoriaGrupoProps = {
	readonly aberto: boolean;
	readonly grupo: AuditoriaGrupoRef | null;
	readonly itens: IAuditoriaEventoGrupo[];
	readonly carregando: boolean;
	readonly paginacao: {
		readonly pagina: number;
		readonly totalPaginas: number;
		readonly total: number;
	};
	readonly acoes: {
		readonly onFechar: () => void;
		readonly onMudarPagina: (pagina: number) => void;
		readonly onPesquisar: (filtros: FiltroAuditoriaAplicado) => void;
	};
};

export default function ModalAuditoriaGrupo({
	aberto,
	grupo,
	itens,
	carregando,
	paginacao,
	acoes,
}: ModalAuditoriaGrupoProps) {
	// Controla o texto do estado vazio (com/sem filtro aplicado), sem expor isso ao container.
	const [temFiltro, setTemFiltro] = useState(false);

	useEffect(() => {
		if (aberto) setTemFiltro(false);
	}, [aberto]);

	const aoPesquisar = (filtros: FiltroAuditoriaAplicado) => {
		setTemFiltro(filtros.tipo !== "todos" || filtros.dataInicio !== "" || filtros.dataFim !== "");
		acoes.onPesquisar(filtros);
	};

	const grupoNome = grupo?.descricao ?? "";
	const totalLabel = paginacao.total === 1 ? "1 evento" : `${paginacao.total} eventos`;
	const subtitulo = carregando ? "Carregando…" : totalLabel;

	return (
		<AppDialogDefault
			aberto={aberto}
			onFechar={acoes.onFechar}
			titulo={`Auditoria · ${grupoNome}`}
			subtitulo={subtitulo}
			icone="lucide:scroll-text"
			maxWidth="lg"
			exibirRodape={false}
			bodyClassName="overflow-hidden"
			paperClassName="min-h-[80vh] max-h-[80vh]"
		>
			<div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden bg-muted/30 p-4 font-sans">
				<FiltrosAuditoria aberto={aberto} carregando={carregando} onPesquisar={aoPesquisar} />

				<section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-border/60 bg-card shadow-sm">
					<div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 px-3.5 py-2.5">
						<div className="flex min-w-0 items-center gap-2">
							<span className="flex shrink-0 items-center justify-center text-primary">
								<FuseSvgIcon size={18}>lucide:scroll-text</FuseSvgIcon>
							</span>

							<div className="min-w-0">
								<h2 className="text-sm font-bold leading-5 text-foreground">Histórico</h2>
								<p className="truncate text-xs font-medium leading-4 text-muted-foreground">
									{grupoNome}
								</p>
							</div>
						</div>

						<span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
							{totalLabel}
						</span>
					</div>

					<div className="min-h-0 flex-1 overflow-y-auto p-3">
						<ListaAuditoria itens={itens} carregando={carregando} temFiltro={temFiltro} />
					</div>

					{paginacao.totalPaginas > 1 ? (
						<div className="flex shrink-0 flex-col gap-2 border-t border-border/60 px-3.5 py-2 sm:flex-row sm:items-center sm:justify-between">
							<span className="text-xs font-medium text-muted-foreground">
								Página {paginacao.pagina} de {paginacao.totalPaginas}
							</span>

							<Pagination
								size="small"
								variant="outlined"
								count={paginacao.totalPaginas}
								page={paginacao.pagina}
								disabled={carregando}
								onChange={(_, p) => acoes.onMudarPagina(p)}
								siblingCount={0}
								boundaryCount={1}
								aria-label="Paginação da auditoria do grupo"
							/>
						</div>
					) : null}
				</section>
			</div>
		</AppDialogDefault>
	);
}
